import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ApiError, useAuth } from '@forbiz/web-kit'
import type { OrderListItem } from '@forbiz/contracts'

import { SelectMenu } from '../components/SelectMenu.js'
import { ordersApi } from '../lib/orders.js'
import { triggerDownload, toQuery } from '../lib/download.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'

/**
 * 기업 주문 목록(BIZ-10). 역할별 필드는 서버가 비운다 —
 * 상태는 항상 채워지는 companyFacingStatus를 라벨로 쓰고, null 필드(amount 등)는
 * 빈칸으로 둔다. 업체 응답은 companyName/amount가 null이라 자연히 노출되지 않는다.
 */

const LIMIT = 20

const occasionLabel: Record<OrderListItem['occasionType'], string> = {
  FUNERAL: '근조',
  CELEBRATION: '축하',
  GIFT: '난·기타',
}

const occasionClass: Record<OrderListItem['occasionType'], string | undefined> = {
  FUNERAL: styles.occFuneral,
  CELEBRATION: styles.occCelebrate,
  GIFT: styles.occGift,
}

/** 희망 시간대 코드 → 사람이 읽는 문구 (상세 화면과 동일). */
const timeSlotLabel: Record<string, string> = {
  BEFORE_12: '12시 이전',
  BEFORE_14: '14시 이전',
  BEFORE_16: '16시 이전',
  ANYTIME: '시간 무관',
}

/** 유형 필터 세그먼트. 빈 값은 전체. */
const OCCASION_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'FUNERAL', label: '근조' },
  { value: 'CELEBRATION', label: '축하' },
  { value: 'GIFT', label: '난·기타' },
]

/** 기간 프리셋 세그먼트(기업 전용). 희망일(desired_date) 기준. 빈 값은 전체 기간. */
const PERIOD_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '전체 기간' },
  { value: 'thisMonth', label: '이번 달' },
  { value: 'lastMonth', label: '지난 달' },
  { value: 'thisQuarter', label: '이번 분기' },
  { value: 'custom', label: '직접 범위' },
]

/** 기업 노출 상태 필터 옵션(계약 companyFacingStatusFilter와 일치). 빈 값은 전체. */
const COMPANY_STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '전체 상태' },
  { value: '작성 중', label: '작성 중' },
  { value: '주문 접수', label: '주문 접수' },
  { value: '확인 필요', label: '확인 필요' },
  { value: '준비 중', label: '준비 중' },
  { value: '배송 중', label: '배송 중' },
  { value: '배송 완료', label: '배송 완료' },
  { value: '배송 문제', label: '배송 문제' },
  { value: '취소 완료', label: '취소 완료' },
]

/** 로컬 날짜 → YYYY-MM-DD (사용자 브라우저 시간대 기준). */
function isoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** 기간 프리셋 → 희망일 범위(from·to). custom이면 사용자가 고른 두 날짜를 그대로 쓴다. */
function periodRange(period: string, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  if (period === 'thisMonth') return { from: isoDate(new Date(y, m, 1)), to: isoDate(new Date(y, m + 1, 0)) }
  if (period === 'lastMonth') return { from: isoDate(new Date(y, m - 1, 1)), to: isoDate(new Date(y, m, 0)) }
  if (period === 'thisQuarter') {
    const qs = Math.floor(m / 3) * 3
    return { from: isoDate(new Date(y, qs, 1)), to: isoDate(new Date(y, qs + 3, 0)) }
  }
  if (period === 'custom') {
    return { ...(customFrom ? { from: customFrom } : {}), ...(customTo ? { to: customTo } : {}) }
  }
  return {}
}

/**
 * 기업 목록 A1 — 3밴드 분류(조치 필요 / 진행 중 / 지난 주문). companyFacingStatus에
 * 결제뱃지(UNPAID)를 얹어 "지금 내가 할 일 → 오고 있는 것 → 끝난 것" 순으로 가른다.
 * 활성 주문은 최신이라 createdAt 내림차순 첫 페이지에 모여, 페이지 단위로 밴드가 성립한다.
 */
type OrderBand = 'action' | 'progress' | 'done'
const ACTION_STATUSES = new Set(['작성 중', '확인 필요', '배송 문제'])
const PROGRESS_STATUSES = new Set(['주문 접수', '준비 중', '배송 중'])
function bandOf(it: OrderListItem): OrderBand {
  if (it.paymentBadge === 'UNPAID') return 'action'
  if (ACTION_STATUSES.has(it.companyFacingStatus)) return 'action'
  if (PROGRESS_STATUSES.has(it.companyFacingStatus)) return 'progress'
  return 'done'
}

/**
 * 업체 목록 밴드 — 업체 관점 상태(it.status)로 "지금 할 일 → 진행 중 → 끝난 것"을 가른다.
 * 기업 3밴드(조치/진행/지난)와 같은 언어. 접수 대기·배송 문제가 최상단 '확인 필요' 밴드다.
 */
const VENDOR_WAITING = new Set(['PO_SENT', 'DELIVERY_ISSUE'])
const VENDOR_PROGRESS = new Set(['VENDOR_ACCEPTED', 'PREPARING', 'DELIVERING'])
function vendorBandOf(it: OrderListItem): OrderBand {
  const s = it.status ?? ''
  if (VENDOR_WAITING.has(s)) return 'action'
  if (VENDOR_PROGRESS.has(s)) return 'progress'
  return 'done'
}

/** 조치 필요 행의 단일 액션 — 무엇을 눌러야 하는지 하나로 좁힌다. */
function actionFor(it: OrderListItem): { label: string; primary: boolean } {
  if (it.companyFacingStatus === '작성 중') return { label: '이어쓰기', primary: true }
  if (it.paymentBadge === 'UNPAID') return { label: '결제하기', primary: true }
  if (it.companyFacingStatus === '확인 필요') return { label: '확인', primary: true }
  if (it.companyFacingStatus === '배송 문제') return { label: '문의', primary: false }
  return { label: '보기', primary: false }
}

/** 진행 중 카드의 도착 히어로 — 배송희망일을 사람 말로. 근조 불안(제때 도착)에 정면 대응. */
function arrivalLabel(desiredDate: string | null): string {
  if (!desiredDate) return '배송일 조율 중'
  const d = new Date(desiredDate)
  if (Number.isNaN(d.getTime())) return desiredDate
  const now = new Date()
  const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const diff = Math.round((d0 - t0) / 86400000)
  if (diff === 0) return '오늘 도착 예정'
  if (diff === 1) return '내일 도착 예정'
  if (diff < 0) return `${d.getMonth() + 1}월 ${d.getDate()}일 도착 예정`
  return `${d.getMonth() + 1}월 ${d.getDate()}일 도착 예정`
}

/** 진행 중 도착 아이콘 — 배송 중은 트럭, 그 전은 달력. */
const ARRIVAL_CAL = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3.5 9h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

/** 업체 카드 배송지 핀 아이콘(라인). */
const PIN_ICON = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

/**
 * 업체 카드 날짜 레일 파츠 — 왼쪽 딥티일 레일에 요일/일/시간대를 나눠 얹는다.
 * 오늘·내일은 요일 대신 상대 표기로 시간 압박을 즉시 드러낸다(근조 = 제때 배송).
 */
function railParts(desiredDate: string | null): { top: string; day: string; dow: string | null } | null {
  if (!desiredDate) return null
  // 'YYYY-MM-DD'를 new Date()에 그대로 넣으면 UTC 자정으로 읽혀 시간대에 따라 하루 밀린다 — 로컬 날짜로 만든다.
  const [yy, mm, dd] = desiredDate.slice(0, 10).split('-').map(Number)
  const d = new Date(yy!, (mm ?? 1) - 1, dd ?? 1)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const diff = Math.round((d0 - t0) / 86400000)
  const dow = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
  const day = String(d.getDate())
  const month = `${d.getMonth() + 1}월`
  // 오늘·내일은 상대 표기. 앞으로의 주문은 월+일(윗줄=월) + 시간대 옆에 요일,
  // 지난 주문(완료 등)은 월만 — 담백하게. 월과 일이 세로로 붙어 한 덩어리로 읽힌다.
  if (diff === 0) return { top: '오늘', day, dow: null }
  if (diff === 1) return { top: '내일', day, dow: null }
  if (diff > 1) return { top: month, day, dow: dow ?? null }
  return { top: month, day, dow: null }
}

/** 조치 필요 밴드 헤더 경고 아이콘(라인). */
const BAND_WARN = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3.5 21 19H3l9-15.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16.6" r="0.4" fill="currentColor" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

const STAT_ICON = {
  total: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  progress: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  attention: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M10.3 3.9 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ),
  done: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m5 13 4 4 10-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  inbox: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13 7 5h10l2 8v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M5 13h4l1 2.5h4L15 13h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  truck: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h11v9H3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 9h4l3 3v3h-7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="7" cy="17.5" r="1.7" stroke="currentColor" strokeWidth="2" />
      <circle cx="17" cy="17.5" r="1.7" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
} as const

/**
 * 업체 목록 상단 요약 (업체 전용). 집계는 서버가 준다(/api/orders/vendor-summary).
 * 큰 카드 3장 대신 구분선 있는 한 장 스트립 — 모바일에서 한 눈에 들어오게(리포트 요약과 같은 결).
 */
function VendorSummaryStrip({ refreshKey }: { refreshKey: number }) {
  // refreshKey가 바뀌면(인라인 주문 접수 등) 버킷을 다시 센다 — 숫자가 목록과 어긋나지 않게.
  const { data } = useAsync(() => ordersApi.vendorSummary(), [refreshKey])
  const fmt = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString('ko-KR'))
  const cells = [
    { key: 'waiting', label: '접수 대기', value: data?.waiting, tone: styles.vsumWait },
    { key: 'inProgress', label: '배송 진행', value: data?.inProgress, tone: styles.vsumProg },
    { key: 'done', label: '이번 달 완료', value: data?.doneThisMonth, tone: styles.vsumDone },
  ]
  return (
    <div className={styles.vsum}>
      {cells.map((c) => (
        <div key={c.key} className={styles.vsumCell}>
          <div className={`${styles.vsumNum} ${c.tone}`}>{fmt(c.value)}</div>
          <div className={styles.vsumLbl}>{c.label}</div>
        </div>
      ))}
    </div>
  )
}

/** companyFacingStatus 문구를 칩 색으로 매핑. 취소·문제를 완료보다 먼저 본다
 *  ('취소 완료'가 '완료'에 걸려 성공색이 되지 않도록). */
function statusChipClass(status: string): string | undefined {
  if (status.includes('취소') || status.includes('문제')) return styles.chipDanger
  if (status.includes('완료')) return styles.chipSuccess
  if (status.includes('확인 필요')) return styles.chipWarning
  return styles.chipInfo
}

/** 목록은 최근 주문 위주라 시안대로 MM.DD로 짧게 — 값이 없으면 대시. */
function fmtDate(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}.${dd}`
}

function fmtWon(n: number | null): string {
  return n === null ? '—' : `${n.toLocaleString('ko-KR')}원`
}

/** 배송 완료 시각 — 카드에 짧게 MM.DD HH:mm. 값이 없으면 대시. */
function fmtDateTime(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${mm}.${dd} ${hh}:${mi}`
}

/**
 * 업체 카드 상태 pill — 내부 상태를 업체 관점 라벨·색으로 보여준다.
 * 기업 노출 상태(companyFacingStatus, 예: PO_SENT→'준비 중')와 달리, 업체에겐
 * 자기가 할 일이 드러나야 한다(PO_SENT→'접수 대기'). 스탯 스트립 버킷과도 문구가 맞는다.
 */
function vendorStatusChip(status: string | null, fallback: string): { label: string; cls: string | undefined } {
  switch (status) {
    case 'PO_SENT':
      return { label: '접수 대기', cls: styles.chipWarning }
    case 'VENDOR_ACCEPTED':
      return { label: '접수함', cls: styles.chipInfo }
    case 'PREPARING':
      return { label: '상품 준비', cls: styles.chipInfo }
    case 'DELIVERING':
      return { label: '배송 중', cls: styles.chipInfo }
    case 'DELIVERED':
      return { label: '배송 완료', cls: styles.chipSuccess }
    case 'VENDOR_REJECTED':
      return { label: '처리 불가', cls: styles.chipDanger }
    case 'DELIVERY_ISSUE':
      return { label: '배송 문제', cls: styles.chipDanger }
    default:
      return { label: fallback, cls: styles.chipInfo }
  }
}

/**
 * 업체 배정 주문 카드(BIZ-7 목록). 발주 정보(vendorCard)를 풍부하게 보여주고,
 * 상태별 액션을 건다. 유일한 인라인 동작은 '주문 접수'(PO_SENT→VENDOR_ACCEPTED) —
 * 나머지는 상세로 이동해 전이/사진 플로우(VendorActionPanel·PhotosSection)를 탄다.
 */
function VendorCard({ it, onReload }: { it: OrderListItem; onReload: () => void }) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const vc = it.vendorCard
  const status = it.status
  const go = () => navigate(`/orders/${it.id}`)

  const accept = async () => {
    setBusy(true)
    setErr(null)
    try {
      await ordersApi.transition(it.id, { to: 'VENDOR_ACCEPTED', forced: false })
      onReload() // 목록 재조회 → 카드 상태·버킷 갱신
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '처리하지 못했습니다')
      setBusy(false)
    }
  }

  // 상태별 부가정보 한 줄 — 라벨(굵게) + 값. 96px 표 대신 담백한 인라인.
  const metaLine = (label: string, value: string) => (
    <div className={styles.vcardMetaLine}>
      <span className={styles.vcardMetaLabel}>{label}</span>
      {value}
    </div>
  )

  const title = vc?.vendorProductName || it.title
  const slot = vc?.timeSlot ? (timeSlotLabel[vc.timeSlot] ?? vc.timeSlot) : ''
  // 근조는 장례식장명+빈소, 없으면(난·기타 등) 도로명 주소로 폴백한다(BIZ-32 #5).
  const place =
    [vc?.deliveryPlaceName, vc?.mortuaryRoom].filter(Boolean).join(' ') ||
    [vc?.deliveryAddress, vc?.deliveryAddressDetail].filter(Boolean).join(' ')

  const isDelivered = status === 'DELIVERED'
  const isDelivering = status === 'DELIVERING'

  // 상태별 마지막 정보 행: 완료 > 배송 중(기사) > 발주 전(리본).
  let lastRow: React.ReactNode = null
  if (vc) {
    if (isDelivered) {
      const when = vc.completedAt ? fmtDateTime(vc.completedAt) : '배송 완료'
      lastRow = metaLine('완료', `${when} · 사진 ${vc.photoCount}장`)
    } else if (isDelivering && vc.driverName) {
      lastRow = metaLine('기사', `${vc.driverName}${vc.driverPhone ? ` · ${vc.driverPhone}` : ''}`)
    } else if (vc.ribbonLine1 || vc.ribbonLine2) {
      lastRow = metaLine('리본', [vc.ribbonLine1, vc.ribbonLine2].filter(Boolean).join(' / '))
    }
  }

  let actions: React.ReactNode
  if (status === 'PO_SENT') {
    actions = (
      <>
        <button data-variant="primary" disabled={busy} onClick={accept}>
          {busy ? '처리 중…' : '주문 접수'}
        </button>
        <button data-variant="secondary" disabled={busy} onClick={go}>
          처리 불가
        </button>
      </>
    )
  } else if (isDelivering) {
    actions = (
      <>
        <button data-variant="primary" onClick={go}>
          배송 완료 처리
        </button>
        <button data-variant="secondary" onClick={go}>
          사진 업로드
        </button>
      </>
    )
  } else if (isDelivered) {
    actions = (
      <button data-variant="secondary" onClick={go}>
        상세 보기
      </button>
    )
  } else {
    actions = (
      <button data-variant="primary" onClick={go}>
        처리하기
      </button>
    )
  }

  const waiting = status === 'PO_SENT' || status === 'DELIVERY_ISSUE'
  const rail = railParts(it.desiredDate)

  return (
    <div className={waiting ? `${styles.vcard} ${styles.vcardWaiting}` : styles.vcard}>
      {/* 딥티일 날짜 레일 — 언제 배송할지가 한눈에. 없으면 '조율 중'. */}
      <div className={styles.vcardRail} aria-hidden="true">
        {rail ? (
          <>
            <span className={styles.vcardRailTop}>{rail.top}</span>
            <span className={styles.vcardRailDay}>{rail.day}</span>
            {(() => {
              // 요일(앞으로의 주문에만) + 시간대를 한 칩에 — 월/일과 분리해 읽는다.
              const sub = [rail.dow, slot].filter(Boolean).join(' · ')
              return sub ? <span className={styles.vcardRailSlot}>{sub}</span> : null
            })()}
          </>
        ) : (
          <span className={styles.vcardRailNone}>희망일 조율 중</span>
        )}
      </div>
      <div className={styles.vcardMain}>
        <div className={styles.vcardTop}>
          <span className={`${styles.occ} ${occasionClass[it.occasionType]}`}>
            {occasionLabel[it.occasionType]}
          </span>
          {(() => {
            const v = vendorStatusChip(status, it.companyFacingStatus)
            return <span className={`${styles.chip} ${v.cls}`}>{v.label}</span>
          })()}
        </div>
        <div className={styles.vcardTitle}>{title}</div>
        <div className={styles.vcardPlace}>
          <span className={styles.vcardPlaceIco} aria-hidden="true">
            {PIN_ICON}
          </span>
          <span>{place || '배송지 미정'}</span>
        </div>
        {lastRow}
        <div className={styles.vcardActions}>{actions}</div>
        {err ? (
          <div className={styles.error} role="alert">
            {err}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function OrdersPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const isAdmin = user?.role === 'COMPANY_ADMIN'
  const isVendor = user?.role === 'VENDOR_MEMBER'
  const companyId = user?.companyId ?? null

  // 홈 알림 스트립(BIZ-99)에서 넘어오면 상태 필터를 미리 건다(예: 확인 필요).
  // 필터만 조용히 걸면 평소 목록과 똑같아 보여서 "눌렀는데 아무 일도 없다"가 된다 —
  // 착지 컨텍스트 배너(fromAlert)로 '왜 이 화면인지'를 최상단에서 즉시 말해준다.
  const location = useLocation()
  const initialCompanyStatus = (location.state as { companyStatus?: string } | null)?.companyStatus ?? ''
  const [fromAlert, setFromAlert] = useState(Boolean(initialCompanyStatus))

  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [occasionType, setOccasionType] = useState('')
  const [period, setPeriod] = useState('')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [companyStatus, setCompanyStatus] = useState(initialCompanyStatus)
  const [offset, setOffset] = useState(0)
  const [vendorRefresh, setVendorRefresh] = useState(0)
  // 검색·필터는 기본 접힘 — 주문 적은 회사는 필터를 볼 일이 없어 상단 소음만 된다.
  const [filtersOpen, setFiltersOpen] = useState(false)

  const { from, to } = periodRange(period, customFrom, customTo)

  const { data, loading, error, reload } = useAsync(
    () =>
      ordersApi.list({
        ...(q ? { q } : {}),
        ...(occasionType ? { occasionType } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(companyStatus ? { companyStatus } : {}),
        // 주문 내역 기본 정렬: 주문일(접수 생성일) 내림차순 = 최신 주문이 맨 위.
        sort: 'createdAt',
        dir: 'desc',
        limit: LIMIT,
        offset,
      }),
    [q, occasionType, from, to, companyStatus, offset],
  )

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setQ(qInput.trim())
    setOffset(0)
  }

  const onOccasionPick = (value: string) => {
    setOccasionType(value)
    setOffset(0)
  }

  const onPeriodPick = (value: string) => {
    setPeriod(value)
    if (value !== 'custom') {
      setCustomFrom('')
      setCustomTo('')
    }
    setOffset(0)
  }

  const onStatusPick = (value: string) => {
    setCompanyStatus(value)
    setOffset(0)
    setFromAlert(false) // 사용자가 직접 필터를 만지면 착지 배너의 소임은 끝
  }

  const hasFilters = Boolean(q || occasionType || period || companyStatus)
  const resetFilters = () => {
    setQInput('')
    setQ('')
    setOccasionType('')
    setPeriod('')
    setCustomFrom('')
    setCustomTo('')
    setCompanyStatus('')
    setOffset(0)
    setFromAlert(false)
  }

  // 인라인 주문 접수 후 목록과 업체 스탯 스트립을 함께 새로고침한다.
  const reloadVendor = () => {
    reload()
    setVendorRefresh((k) => k + 1)
  }

  const total = data?.total ?? 0
  const items = data?.items ?? []
  const hasPrev = offset > 0
  const hasNext = offset + LIMIT < total

  // 기업 목록 3밴드(현재 페이지 기준). 활성 주문은 최신이라 첫 페이지에 모인다.
  const actionItems = items.filter((it) => bandOf(it) === 'action')
  const progressItems = items.filter((it) => bandOf(it) === 'progress')
  const doneItems = items.filter((it) => bandOf(it) === 'done')

  // 업체 목록 3밴드(현재 페이지 기준) — it.status 기준으로 할 일을 위로.
  const vWaiting = items.filter((it) => vendorBandOf(it) === 'action')
  const vProgress = items.filter((it) => vendorBandOf(it) === 'progress')
  const vDone = items.filter((it) => vendorBandOf(it) === 'done')

  // 업체 화면을 else 기본값으로 두지 않는다(BIZ-34). role이 확정되기 전이거나
  // 기업/업체 어느 쪽도 아닐 때는 안전한 기본(로딩 자리)만 보이고, 업체 화면은
  // 아래에서 isVendor일 때만 명시적으로 그린다 — 판정이 순간 어긋나도 업체 화면으로
  // 새지 않게. (기존엔 제목·부제가 isCompany ? 기업 : 업체 구조라 업체가 else였다.)
  if (!isCompany && !isVendor) {
    return (
      <main>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>주문</h1>
          </div>
        </div>
        <p className={styles.muted}>불러오는 중…</p>
      </main>
    )
  }

  return (
    <main>
      {/* 홈 알림 착지 컨텍스트 — 어떤 필터로 좁혀 보고 있는지 최상단에서 말해준다. */}
      {fromAlert && companyStatus ? (
        <div className={styles.landing} role="status">
          <span className={styles.landingIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 3.5 21 19H3l9-15.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="16.6" r="0.4" fill="currentColor" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </span>
          <span className={styles.landingText}>
            <b>{companyStatus}</b> 주문만 보고 있어요{data ? ` · ${total}건` : ''}
          </span>
          <button type="button" className={styles.landingClear} onClick={resetFilters}>
            전체 보기
          </button>
        </div>
      ) : null}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{isVendor ? '배정 주문' : '주문 내역'}</h1>
          <p className={styles.subtitle}>
            {isVendor ? '우리 업체에 배정된 화환 주문' : '우리 회사가 접수한 화환·난 주문'}
          </p>
        </div>
        {/* 주문하기 버튼은 두지 않는다 — 주문 진입은 홈 히어로가 정본(이 화면은 조회용).
            엑셀 내보내기는 검색·필터 툴바로 옮겼다(데스크톱 전용). */}
      </div>

      {isVendor ? <VendorSummaryStrip refreshKey={vendorRefresh} /> : null}

      {/* 기업: 검색·필터는 기본 접힘. 활성 주문(조치·진행)은 필터 없이 바로 보이고,
          긴 이력을 뒤질 때만 펼친다. 엑셀은 데스크톱 업무 흐름이라 여기 함께 둔다. */}
      {isCompany ? (
        <div className={styles.ordersToolbar}>
          <button
            type="button"
            className={styles.filterToggle}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            검색·필터{hasFilters ? ' · 적용됨' : ''}
          </button>
          {isAdmin ? (
            <button
              data-variant="secondary"
              className={styles.desktopOnly}
              onClick={() =>
                triggerDownload(
                  `/api/orders/export${toQuery({ q, occasionType, from, to, companyStatus })}`,
                )
              }
            >
              엑셀 내보내기
            </button>
          ) : null}
        </div>
      ) : null}

      {isCompany && filtersOpen ? (
        <form onSubmit={onSearch} className={styles.filterPanel}>
          <div className={styles.searchBox}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="고인명·상주명·행사대상·수령인·장례식장 검색"
              aria-label="주문 통합검색"
            />
          </div>
          <div className={styles.segmented} role="group" aria-label="주문 유형 필터">
            {OCCASION_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={occasionType === f.value}
                className={
                  occasionType === f.value ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn
                }
                onClick={() => onOccasionPick(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <SelectMenu
            value={companyStatus}
            options={COMPANY_STATUS_FILTERS}
            onChange={onStatusPick}
            ariaLabel="상태 필터"
          />
          <div className={styles.segmented} role="group" aria-label="기간 필터">
            {PERIOD_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={period === f.value}
                className={
                  period === f.value ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn
                }
                onClick={() => onPeriodPick(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {period === 'custom' ? (
            <div className={styles.dateRange}>
              <input
                type="date"
                aria-label="시작일"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => {
                  setCustomFrom(e.target.value)
                  setOffset(0)
                }}
              />
              <span className={styles.muted}>~</span>
              <input
                type="date"
                aria-label="종료일"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => {
                  setCustomTo(e.target.value)
                  setOffset(0)
                }}
              />
            </div>
          ) : null}
          {hasFilters ? (
            <button type="button" className={styles.filterReset} onClick={resetFilters}>
              필터 초기화
            </button>
          ) : null}
        </form>
      ) : null}

      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : items.length === 0 ? (
        <div className={styles.empty}>{isVendor ? '배정된 주문이 없습니다' : '주문이 없습니다'}</div>
      ) : (
        <>
          {isVendor ? (
            <>
              {/* 확인 필요 — 방문 이유 1위(새 주문 접수·배송 문제). 0건이면 밴드가 사라진다.
                  상단 스트립 '접수 대기'(PO_SENT만)와 범위가 달라 제목을 따로 둔다. */}
              {vWaiting.length > 0 ? (
                <section className={styles.band}>
                  <div className={styles.bandHead}>
                    <span className={styles.bandIconWarn} aria-hidden="true">
                      {BAND_WARN}
                    </span>
                    <span className={styles.bandTitle}>확인 필요</span>
                    <span className={styles.bandCountWarn}>{vWaiting.length}</span>
                  </div>
                  <div className={styles.vcardGrid}>
                    {vWaiting.map((it) => (
                      <VendorCard key={it.id} it={it} onReload={reloadVendor} />
                    ))}
                  </div>
                </section>
              ) : null}

              {/* 진행 중 — 접수함·준비·배송 중. 오고 있는 주문. */}
              {vProgress.length > 0 ? (
                <section className={styles.band}>
                  <div className={styles.bandHead}>
                    <span className={styles.bandTitle}>진행 중</span>
                    <span className={styles.bandCount}>{vProgress.length}</span>
                  </div>
                  <div className={styles.vcardGrid}>
                    {vProgress.map((it) => (
                      <VendorCard key={it.id} it={it} onReload={reloadVendor} />
                    ))}
                  </div>
                </section>
              ) : null}

              {/* 완료 — 배송 완료·처리 불가. 끝난 것. */}
              {vDone.length > 0 ? (
                <section className={styles.band}>
                  <div className={styles.bandHead}>
                    <span className={styles.bandTitle}>완료</span>
                    <span className={styles.bandCount}>{vDone.length}</span>
                  </div>
                  <div className={styles.vcardGrid}>
                    {vDone.map((it) => (
                      <VendorCard key={it.id} it={it} onReload={reloadVendor} />
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
          <>
            {/* 1. 조치 필요 — 방문 이유 1위. 각 행에 단일 액션. 0건이면 밴드 자체가 사라진다. */}
            {actionItems.length > 0 ? (
              <section className={styles.band}>
                <div className={styles.bandHead}>
                  <span className={styles.bandIconWarn} aria-hidden="true">
                    {BAND_WARN}
                  </span>
                  <span className={styles.bandTitle}>조치 필요</span>
                  <span className={styles.bandCountWarn}>{actionItems.length}</span>
                </div>
                <div className={styles.bandCard}>
                  {actionItems.map((it) => {
                    const a = actionFor(it)
                    // 행 전체는 늘 상세로(다른 행과 일관). 액션 버튼만 특화 타깃으로 —
                    // 작성 중이면 곧장 이어쓰기(/edit), 나머지는 상세에서 처리.
                    const detailTo = `/orders/${it.id}`
                    const actionTo =
                      it.companyFacingStatus === '작성 중' ? `/orders/${it.id}/edit` : detailTo
                    return (
                      <div
                        key={it.id}
                        className={styles.actionRow}
                        onClick={() => navigate(detailTo)}
                        role="link"
                        tabIndex={0}
                        aria-label={`주문 ${it.orderNo ?? '임시저장'} 상세 보기`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(detailTo)
                          }
                        }}
                      >
                        <div className={styles.actionMain}>
                          <div className={styles.ocardTop}>
                            <span className={`${styles.occ} ${occasionClass[it.occasionType]}`}>
                              {occasionLabel[it.occasionType]}
                            </span>
                            {it.paymentBadge === 'UNPAID' ? (
                              <span className={`${styles.chip} ${styles.chipWarning}`}>결제 필요</span>
                            ) : (
                              <span className={`${styles.chip} ${statusChipClass(it.companyFacingStatus)}`}>
                                {it.companyFacingStatus}
                              </span>
                            )}
                          </div>
                          <div
                            className={it.title ? styles.actionTitle : `${styles.actionTitle} ${styles.muted}`}
                          >
                            {it.title || '작성 중인 주문'}
                          </div>
                        </div>
                        <button
                          data-variant={a.primary ? 'primary' : 'secondary'}
                          className={styles.actionBtn}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(actionTo)
                          }}
                        >
                          {a.label}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </section>
            ) : null}

            {/* 2. 진행 중 — 도착일이 히어로. 근조 불안(제때 도착)에 정면 대응. */}
            {progressItems.length > 0 ? (
              <section className={styles.band}>
                <div className={styles.bandHead}>
                  <span className={styles.bandTitle}>진행 중</span>
                  <span className={styles.bandCount}>{progressItems.length}</span>
                </div>
                <div className={styles.progressList}>
                  {progressItems.map((it) => {
                    const go = () => navigate(`/orders/${it.id}`)
                    return (
                      <div
                        key={it.id}
                        className={styles.progressCard}
                        onClick={go}
                        role="link"
                        tabIndex={0}
                        aria-label={`주문 ${it.orderNo ?? '임시저장'} 상세 보기`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            go()
                          }
                        }}
                      >
                        <div className={styles.ocardTop}>
                          <span className={`${styles.occ} ${occasionClass[it.occasionType]}`}>
                            {occasionLabel[it.occasionType]}
                          </span>
                          <span className={`${styles.chip} ${statusChipClass(it.companyFacingStatus)}`}>
                            {it.companyFacingStatus}
                          </span>
                        </div>
                        <div className={styles.progressTitle}>{it.title || '작성 중인 주문'}</div>
                        <div className={styles.arrival}>
                          <span className={styles.arrivalIcon} aria-hidden="true">
                            {it.companyFacingStatus === '배송 중' ? STAT_ICON.truck : ARRIVAL_CAL}
                          </span>
                          <span className={styles.arrivalText}>{arrivalLabel(it.desiredDate)}</span>
                        </div>
                        {it.amount !== null ? (
                          <div className={styles.progressAmount}>{fmtWon(it.amount)}</div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </section>
            ) : null}

            {/* 3. 지난 주문 — 차분한 이력. 완료·취소. */}
            <section className={styles.band}>
              <div className={styles.bandHead}>
                <span className={styles.bandTitle}>지난 주문</span>
                <span className={styles.bandCount}>{doneItems.length}</span>
              </div>
              {doneItems.length > 0 ? (
                <div className={styles.bandCard}>
                  {doneItems.map((it) => {
                    const go = () => navigate(`/orders/${it.id}`)
                    return (
                      <div
                        key={it.id}
                        className={styles.doneRow}
                        onClick={go}
                        role="link"
                        tabIndex={0}
                        aria-label={`주문 ${it.orderNo ?? '임시저장'} 상세 보기`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            go()
                          }
                        }}
                      >
                        <div className={styles.doneMain}>
                          <div className={styles.ocardTop}>
                            <span className={`${styles.occ} ${occasionClass[it.occasionType]}`}>
                              {occasionLabel[it.occasionType]}
                            </span>
                            <span className={`${styles.chip} ${statusChipClass(it.companyFacingStatus)}`}>
                              {it.companyFacingStatus}
                            </span>
                          </div>
                          <div className={styles.doneTitle}>{it.title || '작성 중인 주문'}</div>
                        </div>
                        <div className={styles.doneRight}>
                          <div
                            className={
                              it.amount === null ? `${styles.doneAmount} ${styles.muted}` : styles.doneAmount
                            }
                          >
                            {fmtWon(it.amount)}
                          </div>
                          <div className={styles.doneDate}>{fmtDate(it.desiredDate ?? it.createdAt)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className={styles.bandEmpty}>
                  {hasFilters ? '조건에 맞는 지난 주문이 없어요' : '완료된 주문이 아직 없어요'}
                </div>
              )}
            </section>
          </>
          )}

          {/* 페이저 — 넘길 페이지가 있을 때만. 한 페이지뿐이면 죽은 버튼 대신 카운트만. */}
          <div className={styles.pager}>
            <span className={styles.muted}>
              총 {total.toLocaleString('ko-KR')}건
              {hasPrev || hasNext ? ` · ${offset + 1}–${offset + items.length}` : ''}
            </span>
            {hasPrev || hasNext ? (
              <div className={styles.pagerBtns}>
                <button
                  type="button"
                  className={styles.pagerBtn}
                  disabled={!hasPrev}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                >
                  ‹ 이전
                </button>
                <button
                  type="button"
                  className={styles.pagerBtn}
                  disabled={!hasNext}
                  onClick={() => setOffset(offset + LIMIT)}
                >
                  다음 ›
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </main>
  )
}
